import { z } from 'zod';
import { CanonicalTweetSchema } from './tweets-canonical.js';

export { CanonicalTweetSchema as TweetGetResponseSchema } from './tweets-canonical.js';

import { NullableIsoDatetimeToDateSchema } from './date-codecs.js';
import { VerifiedTypeSchema } from './followers.js';

export const TweetActionSchema = z.enum(['like', 'unlike', 'retweet', 'unretweet', 'bookmark', 'unbookmark', 'reply']);
export type TweetAction = z.infer<typeof TweetActionSchema>;

export const TweetCreateBodySchema = z.object({
  text: z.string().trim().min(1).max(280),
  replyToTweetId: z.string().trim().min(1).optional(),
});
export type TweetCreateBody = z.infer<typeof TweetCreateBodySchema>;

export const TweetCreateWithMediaBodySchema = TweetCreateBodySchema.extend({
  mediaIds: z.array(z.string().trim().min(1)).max(4).optional(),
});
export type TweetCreateWithMediaBody = z.infer<typeof TweetCreateWithMediaBodySchema>;

export const TweetCreateResponseSchema = z.object({
  success: z.boolean(),
  tweetId: z.string().optional(),
  error: z.string().optional(),
});
export type TweetCreateResponse = z.infer<typeof TweetCreateResponseSchema>;

export const TweetMediaUploadBodySchema = z.object({
  data: z.string().min(1, 'media data required'), // base64
  mimeType: z.string().min(1, 'mimeType required'),
  alt: z.string().max(1000).optional(),
});
export type TweetMediaUploadBody = z.infer<typeof TweetMediaUploadBodySchema>;

export const TweetMediaUploadResponseSchema = z.object({
  success: z.boolean(),
  mediaId: z.string().optional(),
  error: z.string().optional(),
});
export type TweetMediaUploadResponse = z.infer<typeof TweetMediaUploadResponseSchema>;

export const TweetActionsBodySchema = z.object({
  action: TweetActionSchema,
});

export const TweetActionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  redirect: z.string().url().optional(),
});

export const TweetsLoadByIdsQuerySchema = z.object({
  ids: z
    .string()
    .transform((value) => value.split(',').filter((id) => id.trim()))
    .refine((ids) => ids.length > 0, 'At least one tweet ID required')
    .refine((ids) => ids.length <= 100, 'Maximum 100 tweet IDs allowed'),
});

export const TweetsLoadByIdsResponseSchema = z.object({
  tweets: z.array(CanonicalTweetSchema),
  timestamp: z.date().optional(),
});

export const TweetsBatchLoadBodySchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(100),
});

export const TweetsBatchLoadQuerySchema = z.object({
  compact: z.coerce.boolean().optional(),
});

export const TweetsBatchLoadResponseSchema = z.object({
  tweets: z.record(z.string(), CanonicalTweetSchema),
  timestamp: z.date(),
});

export const TweetGetStrategySchema = z.enum(['cache-first', 'cache-only', 'twitter-only']);

export const TweetGetQuerySchema = z.object({
  strategy: TweetGetStrategySchema.optional(),
});

export const TweetDedupeGetResponseSchema = z.object({
  tweetId: z.string(),
  matched: z.boolean(),
  distance: z.number().nullable(),
  threshold: z.number(),
  fastPath: z.enum(['byte-equal', 'hash-equal']).optional(),
  warnings: z.array(z.string()).optional(),
  media: z
    .object({
      url: z.string(),
      hash: z.string().nullable(),
      contentType: z.string(),
    })
    .nullable(),
  linkPreview: z
    .object({
      url: z.string(),
      imageUrl: z.string(),
      hash: z.string().nullable(),
      title: z.string().nullable(),
      description: z.string().nullable(),
      contentType: z.string().nullable(),
    })
    .nullable(),
});

export const TweetConversationGetQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  includeRetweets: z.coerce.boolean().optional(),
  includeReplies: z.coerce.boolean().optional(),
  force: z.enum(['true', 'false']).optional(),
});

export const TweetConversationGetResponseSchema = z.unknown();

export const TweetSummarizeGetQuerySchema = z.object({
  includeRetweets: z.coerce.boolean().optional(),
  summaryLength: z.enum(['brief', 'standard', 'detailed']).optional(),
});

export const TweetSummarizeGetResponseSchema = z.unknown();

export const TweetIdPathParamsSchema = z.object({
  tweetId: z.string(),
});
export type TweetIdPathParams = z.infer<typeof TweetIdPathParamsSchema>;

export const TweetSummarizeBodySchema = z.unknown();
export type TweetSummarizeBody = z.infer<typeof TweetSummarizeBodySchema>;

export const TweetAuthorSummarySchema = z
  .object({
    id: z.string(),
    username: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    profileImageUrl: z.string().nullable().optional(),
    verifiedType: VerifiedTypeSchema.optional(),
  })
  .passthrough()
  .nullable();

export type TweetAuthorSummary = z.infer<typeof TweetAuthorSummarySchema>;

export const TweetSummarySchema = z
  .object({
    id: z.string(),
    text: z.string().optional(),
    authorId: z.string().nullable().optional(),
    author: TweetAuthorSummarySchema.optional(),
    createdAt: NullableIsoDatetimeToDateSchema.optional(),
    likeCount: z.number().optional(),
    retweetCount: z.number().optional(),
    replyCount: z.number().optional(),
    quoteCount: z.number().optional(),
    bookmarkCount: z.number().nullable().optional(),
    impressionCount: z.number().nullable().optional(),
  })
  .passthrough();

export type TweetSummary = z.infer<typeof TweetSummarySchema>;

export const TweetSummaryBoundarySchema = TweetSummarySchema.transform((tweet) => ({
  ...tweet,
  text: tweet.text ?? '',
  authorId: tweet.authorId ?? null,
  author: tweet.author ?? null,
  createdAt: tweet.createdAt ?? null,
}));

export type TweetSummaryParsed = z.infer<typeof TweetSummaryBoundarySchema>;

export const TweetsLoadByIdsCliResponseSchema = TweetsLoadByIdsResponseSchema.extend({
  tweets: z.array(TweetSummaryBoundarySchema),
});

export type TweetsLoadByIdsCliResponse = z.infer<typeof TweetsLoadByIdsCliResponseSchema>;
export type TweetsLoadByIdsCliResponseInput = z.input<typeof TweetsLoadByIdsCliResponseSchema>;

export function parseTweetsLoadByIdsCliResponse(input: unknown): TweetsLoadByIdsCliResponse {
  return TweetsLoadByIdsCliResponseSchema.parse(input);
}
